import { FormArray, FormBuilder, FormGroup, ValidationErrors } from '@angular/forms';
import { addMinutes, format, parse } from 'date-fns';
import { computed, effect, inject, Injectable, Injector, runInInjectionContext, Signal, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { map, merge } from 'rxjs';
import { IEnrollmentSchedule } from '@lb/api/mylb/interfaces/ienrollment-summary.interface';
import { ITimeZone } from '@lb/api/mylb/interfaces/itime-zone.interface';
import { SchedulerValidators } from '@lb/components';
import { TimezoneStateService } from '@lb/state/timezone-state.service';

export type ScheduleCalendarForm = {
	id: string;
	dayIndex: number;
	timeID: string;
	startTime: string;
	endTime: string;
};

export type ScheduleTimeSlot = { dayIndex: number; startTime: string; endTime: string };

@Injectable({
	providedIn: 'root'
})
export class SchedulerFormService {
	private readonly _formBuilder = inject(FormBuilder);
	private readonly _injector = inject(Injector);
	private readonly _timezoneStateService = inject(TimezoneStateService);

	private readonly _wizardForm = signal<FormGroup | null>(null);

	private readonly _step1ValidationSignal = signal<boolean>(false);
	private readonly _step2ValidationSignal = signal<boolean>(false);
	private readonly _step3ValidationSignal = signal<boolean>(false);
	private readonly _step4ValidationSignal = signal<boolean>(false);

	// Error signals to track validation errors reactively
	private readonly _step1ErrorsSignal = signal<ValidationErrors | null>(null);
	private readonly _step2ErrorsSignal = signal<ValidationErrors | null>(null);
	private readonly _step3ErrorsSignal = signal<ValidationErrors | null>(null);
	private readonly _step4ErrorsSignal = signal<ValidationErrors | null>(null);

	private readonly _isAdmin = signal<boolean>(false);

	// Form accessors
	readonly wizardForm: Signal<FormGroup | null> = this._wizardForm.asReadonly();
	readonly step1Form: Signal<FormGroup | null> = computed(() => {
		const wizard = this._wizardForm();
		return wizard ? (wizard.get('step1') as FormGroup) : null;
	});
	readonly step2Form: Signal<FormArray | null> = computed(() => {
		const wizard = this._wizardForm();
		return wizard ? (wizard.get('step2') as FormArray) : null;
	});
	readonly step3Form: Signal<FormArray | null> = computed(() => {
		const wizard = this._wizardForm();
		return wizard ? (wizard.get('step3') as FormArray) : null;
	});
	readonly step4Form: Signal<FormGroup | null> = computed(() => {
		const wizard = this._wizardForm();
		return wizard ? (wizard.get('step4') as FormGroup) : null;
	});

	// --- Timezone Signals ---
	private readonly _availableTimezones = this._timezoneStateService.timezones;

	/**
	 * Schedule timezone confirmed in Step 1.
	 *
	 * IMPORTANT: This is NOT necessarily the user's "home" timezone. It's a CONFIRMATION
	 * of the timezone context for THIS schedule. Users may be traveling or may want to
	 * schedule in a different timezone than their usual one.
	 *
	 * Example: A US-based learner traveling in Europe might confirm they want lessons
	 * scheduled in European time during their trip.
	 *
	 * This timezone applies to ALL availability slots in Step 2.
	 *
	 * NOTE: Times are NOT converted when the timezone changes. If a user changes the
	 * timezone, their selected times remain the same (e.g., "9:00 AM" stays "9:00 AM"),
	 * but are now interpreted in the new timezone context.
	 */
	readonly scheduleTimezone: Signal<ITimeZone | null> = computed(() => {
		const step1 = this.step1Form();
		const tzID = step1?.get('timeZone')?.value as number | null;
		const list = this._availableTimezones();
		if (tzID && list?.length) {
			const found = list.find((t) => t.timeZoneID === tzID);
			if (found) return found;
		}
		return null;
	});

	// Form state signals - only for dirty tracking where signals are beneficial
	readonly step1Dirty: Signal<boolean> = computed(() => {
		const step1 = this.step1Form();
		return step1 ? step1.dirty : false;
	});

	readonly step2Dirty: Signal<boolean> = computed(() => {
		const step2 = this.step2Form();
		return step2 ? step2.dirty : false;
	});

	readonly step3Dirty: Signal<boolean> = computed(() => {
		const step3 = this.step3Form();
		return step3 ? step3.dirty : false;
	});

	readonly step4Dirty: Signal<boolean> = computed(() => {
		const step4 = this.step4Form();
		return step4 ? step4.dirty : false;
	});

	/**
	 * Get validation state as a signal - this properly reacts to form changes
	 */
	getStepValidationSignal(stepIndex: number): Signal<boolean> {
		switch (stepIndex) {
			case 0:
				return this._step1ValidationSignal.asReadonly();
			case 1:
				return this._step2ValidationSignal.asReadonly();
			case 2:
				return this._step3ValidationSignal.asReadonly();
			case 3:
				return this._step4ValidationSignal.asReadonly();
			default:
				return signal(false);
		}
	}

	/**
	 * Get validation errors as a signal - this properly reacts to form validation changes
	 */
	getStepErrorsSignal(stepIndex: number): Signal<ValidationErrors | null> {
		switch (stepIndex) {
			case 0:
				return this._step1ErrorsSignal.asReadonly();
			case 1:
				return this._step2ErrorsSignal.asReadonly();
			case 2:
				return this._step3ErrorsSignal.asReadonly();
			case 3:
				return this._step4ErrorsSignal.asReadonly();
			default:
				return signal(null);
		}
	}
	/**
	 * Initialize the complete wizard form
	 */
	initializeWizardForm(isAdmin: boolean = false): void {
		// Prevent multiple initializations
		if (this._wizardForm()) {
			return;
		}

		this._isAdmin.set(isAdmin);

		const wizardForm = this._formBuilder.group({
			step1: this._createStep1Form(),
			step2: this._createStep2Form(),
			step3: this._createStep3Form(),
			step4: this._createStep4Form(),
			isAdmin: [isAdmin]
		});

		this._wizardForm.set(wizardForm);

		// Initialize validation signals after form is created
		this._initializeValidationSignals();
	}

	// --- Timezone helpers ---
	/**
	 * Get schedule items from Step 2 form.
	 * Times are stored as-is without timezone conversion.
	 */
	private _getScheduleItems(step: number): Array<{ dayIndex: number; startTime: string; endTime: string }> {
		let form: FormArray | null = null;
		if (step === 2) {
			form = this.step2Form();
		} else if (step === 3) {
			form = this.step3Form();
		}
		if (!form) return [];

		return form.controls.map((ctrl) => ({
			dayIndex: ctrl.get('dayIndex')?.value,
			startTime: ctrl.get('startTime')?.value,
			endTime: ctrl.get('endTime')?.value
		}));
	}

	/**
	 * Initialize validation signals that track form statusChanges
	 */
	private _initializeValidationSignals(): void {
		const wizard = this._wizardForm();
		if (!wizard) return;

		// Create toSignal subscriptions outside of reactive context
		const step1 = wizard.get('step1') as FormGroup;
		const step2 = wizard.get('step2') as FormGroup;
		const step3 = wizard.get('step3') as FormGroup;
		const step4 = wizard.get('step4') as FormGroup;

		if (step1) {
			runInInjectionContext(this._injector, () => {
				const step1StatusSignal = toSignal(step1.statusChanges, { initialValue: 'INVALID' });
				// Update the validation and error signals whenever status changes
				effect(() => {
					this._step1ValidationSignal.set(step1StatusSignal() === 'VALID');
					this._step1ErrorsSignal.set(step1.errors);
				});
			});
		}

		if (step2) {
			runInInjectionContext(this._injector, () => {
				// Listen to BOTH statusChanges and valueChanges to ensure we capture error updates
				// statusChanges only fires when status changes (VALID <-> INVALID)
				// but errors can change while status remains INVALID
				// ALSO listen to step1 changes since step2 validator depends on step1's frequency
				const step1FrequencyChanges = step1?.get('frequency')?.valueChanges.pipe(map(() => Date.now())) || [];

				// Map to timestamp to ensure unique values trigger the effect
				const step2ChangesSignal = toSignal(
					merge(step2.statusChanges.pipe(map(() => Date.now())), step2.valueChanges.pipe(map(() => Date.now())), step1FrequencyChanges),
					{ initialValue: 0 }
				);
				effect(() => {
					// Trigger on any change
					const timestamp = step2ChangesSignal();
					// When step1 frequency changes, we need to re-validate step2
					if (timestamp !== 0) {
						step2.updateValueAndValidity({ emitEvent: false });
					}
					const status = step2.status;
					this._step2ValidationSignal.set(status === 'VALID');
					// Force re-read of errors to ensure we get the latest
					this._step2ErrorsSignal.set(step2.errors);
				});
			});
		}

		if (step3) {
			runInInjectionContext(this._injector, () => {
				const step1FrequencyChanges = step1?.get('frequency')?.valueChanges.pipe(map(() => Date.now())) || [];

				const step3ChangesSignal = toSignal(
					merge(step3.statusChanges.pipe(map(() => Date.now())), step3.valueChanges.pipe(map(() => Date.now())), step1FrequencyChanges),
					{ initialValue: 0 }
				);
				effect(() => {
					const timestamp = step3ChangesSignal();
					if (timestamp !== 0) {
						step3.updateValueAndValidity({ emitEvent: false });
					}
					this._step3ValidationSignal.set(step3.status === 'VALID');
					this._step3ErrorsSignal.set(step3.errors);
				});
			});
		}

		if (step4) {
			runInInjectionContext(this._injector, () => {
				const step4StatusSignal = toSignal(step4.statusChanges, { initialValue: 'INVALID' });
				effect(() => {
					this._step4ValidationSignal.set(step4StatusSignal() === 'VALID');
					this._step4ErrorsSignal.set(step4.errors);
				});
			});
		}

		// Set initial validation states based on current form validity
		// Don't call updateValueAndValidity() here to avoid triggering validators
		// before parent forms are fully connected
		this._step1ValidationSignal.set(step1?.valid || false);
		this._step2ValidationSignal.set(step2?.valid || false);
		this._step3ValidationSignal.set(step3?.valid || false);
		this._step4ValidationSignal.set(step4?.valid || false);

		// Trigger validation NOW that parent forms are connected
		// This will cause the effects to fire and capture the correct errors
		// We MUST emit events so the statusChanges observables fire
		step1?.updateValueAndValidity();
		step2?.updateValueAndValidity();
		step3?.updateValueAndValidity();
		step4?.updateValueAndValidity();
	}

	/**
	 * Check if a step is valid and can proceed (uses reactive form validation directly)
	 * Use this for synchronous validation checks within methods.
	 * For reactive UI binding, use getStepValidationSignal() instead.
	 */
	canProceedFromStep(stepIndex: number): boolean {
		const wizard = this._wizardForm();
		if (!wizard) {
			return false;
		}

		switch (stepIndex) {
			case 0:
				return wizard.get('step1')?.valid ?? false;
			case 1:
				return wizard.get('step2')?.valid ?? false;
			case 2:
				return wizard.get('step3')?.valid ?? false;
			case 3:
				return wizard.get('step4')?.valid ?? false;
			default:
				return false;
		}
	}

	/**
	 * Get all form data
	 */
	getFormData(): {
		configuration: any;
		preferredSelections: any;
		scheduleSelections: any;
		reviewData: any;
	} {
		const wizard = this._wizardForm();
		if (!wizard) {
			return {
				configuration: {},
				preferredSelections: {},
				scheduleSelections: {},
				reviewData: {}
			};
		}

		return {
			configuration: wizard.get('step1')?.value || {},
			preferredSelections: wizard.get('step2')?.value || {},
			scheduleSelections: wizard.get('step3')?.value || {},
			reviewData: wizard.get('step4')?.value || {}
		};
	}

	/**
	 * Is Preferred Time Slot
	 */
	isPreferredTimeSlot(dayIndex: number, timeID: string): boolean {
		const step2 = this.step2Form();
		if (!step2) return false;

		const selectedSchedules = step2.value as Array<{ dayIndex: number; timeID: string }>;
		return selectedSchedules.find((s) => s.dayIndex === dayIndex && s.timeID === timeID) !== undefined;
	}

	/**
	 * Destroy the form and clean up
	 */
	destroy(): void {
		this._wizardForm.set(null);
	}

	/**
	 * Reset the entire wizard
	 */
	resetWizard(): void {
		this._wizardForm()?.reset();
		this.wizardForm()?.get('isAdmin')?.setValue(this._isAdmin());
		this.initializeWizardForm(this._isAdmin());
	}

	/**
	 * Populate wizard from data
	 */
	populateFromData(data: any): void {
		const wizard = this._wizardForm();

		if (!wizard) {
			return;
		}

		if (data.configuration) {
			wizard.get('step1')?.patchValue(data.configuration);
			this.setOriginalConfigurationData(data.configuration);
		}

		if (this._isAdmin()) {
			if (data.preferredTimeSlots) {
				const step2 = wizard.get('step2') as FormArray;
				step2.clear();
				data.preferredTimeSlots.forEach((slot: ScheduleCalendarForm) => {
					step2.push(
						this._formBuilder.group({
							id: [slot.id],
							dayIndex: [slot.dayIndex],
							timeID: [slot.timeID],
							startTime: [slot.startTime],
							endTime: [slot.endTime]
						})
					);
				});
			}

			if (data.scheduleTimeSlots) {
				const step3 = wizard.get('step3') as FormArray;
				step3.clear();
				data.scheduleTimeSlots.forEach((slot: ScheduleCalendarForm) => {
					step3.push(
						this._formBuilder.group({
							id: [slot.id],
							dayIndex: [slot.dayIndex],
							timeID: [slot.timeID],
							startTime: [slot.startTime],
							endTime: [slot.endTime]
						})
					);
				});
			}
		}

		// Trigger validation for all steps after data population
		wizard.updateValueAndValidity();
	}

	/**
	 * Configuration change management
	 */
	private _originalConfigurationData?: any;

	setOriginalConfigurationData(data: any): void {
		this._originalConfigurationData = { ...data };
	}

	hasConfigurationChanges(): boolean {
		const step1 = this.step1Form();
		if (!step1 || !this._originalConfigurationData) {
			return false;
		}

		const currentData = step1.value;
		return (
			currentData.frequency !== this._originalConfigurationData.frequency ||
			currentData.startDate !== this._originalConfigurationData.startDate ||
			currentData.timeZone !== this._originalConfigurationData.timeZone
		);
	}

	getConfigurationChangesToCommit(): any {
		const step1 = this.step1Form();
		if (!step1 || !this._originalConfigurationData) {
			return {};
		}

		const changes: any = {};
		const currentData = step1.value;

		if (currentData.frequency !== this._originalConfigurationData.frequency) {
			changes.frequency = {
				original: this._originalConfigurationData.frequency,
				new: currentData.frequency
			};
		}

		if (currentData.startDate !== this._originalConfigurationData.startDate) {
			changes.startDate = {
				original: this._originalConfigurationData.startDate,
				new: currentData.startDate
			};
		}

		if (currentData.timeZone !== this._originalConfigurationData.timeZone) {
			changes.timeZone = {
				original: this._originalConfigurationData.timeZone,
				new: currentData.timeZone
			};
		}

		return changes;
	}

	commitConfigurationSuccessful(): void {
		const step1 = this.step1Form();
		if (step1) {
			step1.markAsPristine();
			this._originalConfigurationData = { ...step1.value };
		}
	}

	/**
	 * Compatibility methods for existing code
	 */
	getPendingStartDate(): string | null {
		const step1 = this.step1Form();
		const startDate = step1?.get('startDate')?.value || null;
		return startDate;
	}

	getPendingFrequency(): number | null {
		const step1 = this.step1Form();
		return step1?.get('frequency')?.value || null;
	}

	/**
	 * Schedule selection methods
	 */
	addScheduleSelection(scheduleData: { dayIndex: number; timeID: string }, step: number): void {
		let form: FormArray | null = null;
		if (step === 2) {
			form = this.step2Form();
		} else if (step === 3) {
			form = this.step3Form();
		}

		if (!form) return;

		const endTime = format(addMinutes(parse(scheduleData.timeID, 'HH:mm', new Date()), 30), 'HH:mm');

		// Add the time slot as-is. Times are always displayed and stored in the Step 1 confirmed timezone.
		// No conversion needed - what you see is what you get.
		const group = this._formBuilder.group({
			id: [`${scheduleData.dayIndex}-${scheduleData.timeID}`],
			dayIndex: [scheduleData.dayIndex],
			timeID: [scheduleData.timeID],
			startTime: [scheduleData.timeID],
			endTime: [endTime]
		});
		form.push(group);
		form.updateValueAndValidity();

		if (step === 2) {
			// Also add to step 3
			this.addScheduleSelection(scheduleData, 3);
		}
	}

	// Should match the format ${dayIndex}-${hourID} used in addScheduleSelection
	removeScheduleSelection(scheduleID: string, step: number): void {
		let form: FormArray | null = null;
		if (step === 2) {
			form = this.step2Form();
		} else if (step === 3) {
			form = this.step3Form();
		}

		if (!form) return;

		for (let i = form.length - 1; i >= 0; i--) {
			const scheduleGroup = form.at(i);
			if (scheduleGroup.get('id')?.value === scheduleID) {
				form.removeAt(i);
				break;
			}
		}
		// Trigger validation after array modification
		form.updateValueAndValidity();

		if (step === 2) {
			// Also remove from step 3
			this.removeScheduleSelection(scheduleID, 3);
		}
	}

	// Selection status check methods
	isScheduleSelected(scheduleId: any): boolean {
		const step2 = this.step2Form();
		if (!step2) return false;

		const selectedSchedules = step2.get('selectedSchedules') as FormArray;
		if (!selectedSchedules) return false;

		return selectedSchedules.controls.some((control) => {
			const controlId = control.get('id')?.value;
			return controlId === scheduleId;
		});
	}

	/**
	 * Clear all schedule selections
	 */
	clearAllScheduleSelections(step: number): void {
		let form: FormArray | null = null;
		if (step === 2) {
			form = this.step2Form();
		} else if (step === 3) {
			form = this.step3Form();
		}

		if (!form) return;

		while (form.length > 0) {
			form.removeAt(0);
		}
		// Trigger validation after clearing selections
		form.updateValueAndValidity();
	}

	// Utility methods
	markStepAsTouched(stepIndex: number): void {
		const wizard = this._wizardForm();
		if (!wizard) return;

		const stepName = `step${stepIndex + 1}`;
		wizard.get(stepName)?.markAllAsTouched();
	}

	/**
	 * Force validation to run on a specific step
	 * Useful when data is loaded programmatically and validation needs to be re-evaluated
	 */
	triggerStepValidation(stepIndex: number): void {
		const wizard = this._wizardForm();
		if (!wizard) return;

		const stepName = `step${stepIndex + 1}`;
		wizard.get(stepName)?.updateValueAndValidity();
	}

	/**
	 * Force validation to run on all steps
	 * Useful after loading data or making bulk changes
	 */
	triggerAllValidation(): void {
		const wizard = this._wizardForm();
		if (!wizard) return;

		wizard.updateValueAndValidity();
	}

	/**
	 * Get validation summary for a specific step (direct form access)
	 */
	getValidationSummary(stepIndex: number): any {
		const wizard = this._wizardForm();
		if (!wizard) return { valid: false, errors: {} };

		const stepName = `step${stepIndex + 1}`;
		const stepForm = wizard.get(stepName);

		return {
			valid: stepForm?.valid ?? false,
			errors: stepForm?.errors || {},
			touched: stepForm?.touched ?? false,
			dirty: stepForm?.dirty ?? false
		};
	}

	/**
	 * Groups a flat list of 30-min blocks into merged IEnrollmentSchedule objects.
	 * @param blocks Array of { dayIndex: number, startTime: string, endTime: string }
	 * @param timezoneID The confirmed timezone ID to apply to all schedules (from Step 1)
	 * @returns IEnrollmentSchedule[]
	 */
	mergeBlocksToSchedules(blocks: ScheduleTimeSlot[], timezoneID?: number): Array<IEnrollmentSchedule> {
		// Expect canonical (schedule timezone) blocks already normalized to 30-min contiguous ranges.
		// For safety, we still merge adjacent 30-min segments.
		const sanitizeEnd = (t: string): string => {
			if (!t) return t;
			// API cannot accept 24:00:00; represent end-of-day as 23:59
			if (t === '24:00' || t === '24:00:00') return '23:59';
			return t;
		};
		interface Slot {
			dayIndex: number;
			startTime: string;
			endTime: string;
		}
		const normalized: Record<number, Slot[]> = {};
		blocks.forEach((b) => {
			if (!normalized[b.dayIndex]) normalized[b.dayIndex] = [];
			normalized[b.dayIndex].push({ ...b });
		});

		const schedules: Array<IEnrollmentSchedule> = [];

		Object.keys(normalized).forEach((dayIndexStr) => {
			const dayIndex = parseInt(dayIndexStr, 10);
			const list = normalized[dayIndex].sort(
				(a, b) => parse(a.startTime, 'HH:mm', new Date()).getTime() - parse(b.startTime, 'HH:mm', new Date()).getTime()
			);
			let current = list[0];
			for (let i = 1; i <= list.length; i++) {
				const next = list[i];
				if (next) {
					const currentEnd = parse(current.endTime, 'HH:mm', new Date());
					const nextStart = parse(next.startTime, 'HH:mm', new Date());
					const diff = (nextStart.getTime() - currentEnd.getTime()) / 60000;
					if (diff === 0) {
						// contiguous; extend
						current = { ...current, endTime: next.endTime };
						continue;
					}
				}
				// push current
				schedules.push({
					dayIndex,
					startTime: current.startTime,
					endTime: sanitizeEnd(current.endTime),
					enrollmentID: 0,
					createDate: new Date(),
					timezoneID: timezoneID
				});
				current = next as Slot;
			}
		});

		return schedules;
	}

	/**
	 * Build save payload with the confirmed timezone from Step 1.
	 * Times are used as-is (no conversion), with the Step 1 timezone applied to all.
	 */
	buildScheduleSavePayload(step: number): Array<{ dayIndex: number; startTime: string; endTime: string; timezoneID?: number }> {
		const scheduleItems = this._getScheduleItems(step);
		const scheduleTz = this.scheduleTimezone();
		const timezoneID = scheduleTz?.timeZoneID;

		// Merge 30-min blocks into larger contiguous schedules
		return this.mergeBlocksToSchedules(scheduleItems, timezoneID).map((s) => ({
			dayIndex: s.dayIndex,
			startTime: s.startTime,
			endTime: s.endTime,
			timezoneID: s.timezoneID
		}));
	}

	// Private methods
	private _createStep1Form(): FormGroup {
		const form = this._formBuilder.group(
			{
				frequency: [null],
				startDate: [null],
				timeZone: [null],
				isAdmin: [this._isAdmin()]
			},
			{
				validators: [SchedulerValidators.step1FormValidator] // Add FormGroup validator directly in constructor
			}
		);

		return form;
	}

	private _createStep2Form(): FormArray {
		return this._formBuilder.array([], {
			validators: [SchedulerValidators.step2FormValidator]
		});
	}

	private _createStep3Form(): FormArray {
		return this._formBuilder.array([], {
			validators: [SchedulerValidators.step3FormValidator]
		});
	}

	private _createStep4Form(): FormGroup {
		const form = this._formBuilder.group(
			{
				languageExperience: [null],
				learningNeeds: [null],
				requestedInstructor: [null]
			},
			{
				validators: [SchedulerValidators.step4FormValidator] // Add FormGroup validator directly in constructor
			}
		);

		return form;
	}
}
